// The one dialog primitive. Centered card from `sm` up, bottom sheet below it. Portalled to <body> so it
// is never clipped by an overflow-hidden ancestor, closes on Escape / backdrop click, locks page scroll,
// keeps Tab inside the dialog and returns focus to whatever opened it.
// Hand-rolled `fixed inset-0` overlays elsewhere in the app should migrate to this.
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { CloseIcon } from '@/components/UI/icons';

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Omit to render a plain dialog with only the close (×) affordance — e.g. read-only content. */
  onSave?: () => void;
  saving?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  saveDisabled?: boolean;
  /** Max width of the dialog from `sm` up (default `max-w-lg`). */
  maxWidthClassName?: string;
  /** Optional small note shown left-aligned in the footer, alongside Cancel/Save — e.g. a
   * caveat about when the condition is re-evaluated. Omit for the plain right-aligned buttons. */
  footerLeft?: React.ReactNode;
  /** Use the destructive button style for the confirm action (delete / remove flows). */
  danger?: boolean;
  /** Custom footer content (replaces the default Cancel/Save row), e.g. a form's own submit buttons. */
  footer?: React.ReactNode;
  /** Extra classes for the scrolling body (default `space-y-4 px-5 py-4`; pass `p-0` etc. to override). */
  bodyClassName?: string;
  /** Backdrop click closes the dialog (default). Turn off for editors holding unsaved work; Escape and × still close. */
  closeOnBackdrop?: boolean;
  /** Stay a centred dialog on small screens too, instead of docking as a bottom sheet. */
  centered?: boolean;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export const Modal: React.FC<ModalProps> = ({
  title, onClose, children, onSave, saving, saveLabel = 'Save', cancelLabel = 'Cancel',
  saveDisabled, maxWidthClassName = 'max-w-lg', footerLeft, danger, footer, bodyClassName, closeOnBackdrop = true,
  centered = false,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    // Land on the first form control if there is one, otherwise on the dialog itself.
    const first = panel?.querySelector<HTMLElement>('input,select,textarea') ?? panel;
    first?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!items.length) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      className={cn(
        'modal-scrim fixed inset-0 z-modal flex justify-center sm:items-center sm:p-4',
        centered ? 'items-center p-4' : 'items-end',
      )}
      onMouseDown={(e) => { if (closeOnBackdrop && e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'flex max-h-[90dvh] w-full flex-col border border-ui-border bg-ui-card shadow-pop focus:outline-none',
          centered
            ? 'max-h-[85dvh] rounded-panel'
            : 'rounded-t-panel pb-[env(safe-area-inset-bottom)] sm:max-h-[85dvh] sm:rounded-panel sm:pb-0',
          maxWidthClassName,
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b border-ui-border py-2 pl-5 pr-3">
          <h2 id={titleId} className="text-sm font-semibold text-ui-text">{title}</h2>
          <IconButton label="Close" size="sm" onClick={onClose}>
            <CloseIcon size={16} />
          </IconButton>
        </div>
        <div className={cn('min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4', bodyClassName)}>{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-ui-border px-5 py-3">{footer}</div>
        ) : onSave && (
          <div className={cn('flex items-center border-t border-ui-border px-5 py-3', footerLeft ? 'justify-between' : 'justify-end')}>
            {footerLeft && <p className="text-xs text-ui-text3">{footerLeft}</p>}
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>{cancelLabel}</Button>
              <Button
                size="sm"
                variant={danger ? 'destructive' : 'primary'}
                onClick={onSave}
                disabled={saving || saveDisabled}
                loading={saving}
              >
                {saveLabel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
